import {addBreadcrumb, captureException} from '@sentry/browser';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {extractAppSyncErrorMessage} from './extractAppSyncErrorMessage';
import {isAppSyncErrorPayload} from './isAppSyncErrorPayload';
import {isAuthErrorPayload} from './isAuthErrorPayload';

type NormalizedWebsocketError = {
  error: Error;
  isTransient: boolean;
};

const sanitizeUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
  } catch {
    const [base] = url.split('?');
    return base ?? 'unknown';
  }
};

const isWebSocketEvent = (value: unknown): value is {type: string; target?: unknown} =>
  typeof value === 'object' &&
  value !== null &&
  'type' in value &&
  typeof (value as {type: unknown}).type === 'string';

const isWebSocketTarget = (value: unknown): value is {url?: string; readyState?: number} =>
  typeof value === 'object' && value !== null;

const normalizeError = (error: unknown): NormalizedWebsocketError =>
  match(error)
    .when(
      (e): e is Error => e instanceof Error,
      (e) => ({error: e, isTransient: false})
    )
    // Auth rejections are an expected lifecycle condition, not a bug.
    .when(isAuthErrorPayload, (e) => ({
      error: new Error(extractAppSyncErrorMessage(e)),
      isTransient: true,
    }))
    // Other AppSync errors are still worth reporting — but with a readable message.
    .when(isAppSyncErrorPayload, (e) => ({
      error: new Error(extractAppSyncErrorMessage(e)),
      isTransient: false,
    }))
    .when(isWebSocketEvent, (e) => {
      const target = isWebSocketTarget(e.target) ? e.target : null;
      const rawUrl = target?.url;
      const url = isNotNil(rawUrl) ? sanitizeUrl(rawUrl) : 'unknown';
      const readyState = target?.readyState;
      return {
        error: new Error(`WebSocket ${e.type} event (url: ${url}, readyState: ${readyState})`),
        isTransient: true,
      };
    })
    .otherwise((e) => {
      let error = String(e);

      try {
        error = JSON.stringify(e);
      } catch {}

      return {error: new Error(error), isTransient: false};
    });

export const captureWebsocketError = (error: unknown) => {
  const {error: normalizedError, isTransient} = normalizeError(error);
  console.error('AppSync subscription error:', normalizedError.message);

  if (isAuthErrorPayload(error)) {
    addBreadcrumb({
      category: 'appsync',
      level: 'warning',
      message: normalizedError.message,
    });
    return;
  }

  if (isTransient) {
    return;
  }

  captureException(normalizedError, {
    level: 'error',
    tags: {
      module: 'appsync',
    },
  });
};
